import { Inject, Injectable, InjectionToken } from '@angular/core';
import { LoggerService, UserAccountType } from '@pf/shared-services';
import {
    catchError,
    filter,
    map,
    Observable,
    of,
    switchMap,
    tap,
    throwError,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
    LaneRead,
    LoadRead,
    LoadSearchParams,
} from '@pf/platform-loads/domain';
import {
    NotificationType,
    ProfileProvider,
    SafeAny,
    ToastMessage,
    ToastType,
} from '@pf/shared-common';
import { NotificationsService } from '@pf/shared-ui';
import * as saveAs from 'file-saver';
import { isValue } from '@pf/shared-utility';
import {
    RadiusLaneCreateBody,
    RadiusLaneUpdateBody,
} from '../entities/RadiusLaneRequests';

export const SCHN_FUNCTIONS_API_URL = new InjectionToken<string>(
    'SCHN_FUNCTIONS_API_URL'
);

export interface LoadRateResult {
    LoadId: string;
    Load: LoadRead;
    Message: string;
}

export interface LoadHasLaneRequest {
    pickupStopLocationId: string;
    exportCommodityItemId: string;
    deliveryStopLocationId?: string;
    deliveryStopVendorId?: string;
    loadDate?: string;
}

export interface LoadHasLaneResult {
    hasLane?: boolean;
    status?: 'initial' | 'pending' | 'complete';
    laneId?: string;
}

export interface LoadFinancialsExportRequest {
    includeLoadData: boolean;
    loadsUnifiedQueryString: string;
}

export enum MetabaseDashboard {
    RadiusLoads = 'radius-loads',
    CustomerLoads = 'customer-loads',
}

const DashboardsByUserAccountType: Partial<
    Record<UserAccountType, MetabaseDashboard>
> = {
    [UserAccountType.Administrator]: MetabaseDashboard.RadiusLoads,
    [UserAccountType.Tenant]: MetabaseDashboard.RadiusLoads,
    [UserAccountType.Customer]: MetabaseDashboard.CustomerLoads,
};

@Injectable({ providedIn: 'root' })
export class SchnFunctionsDataService {
    externalLoadsImportApiUrl = `${this.functionsUrl}/api/sai-loads/import`;
    serviceLoadsImportApiUrl = `${this.functionsUrl}/api/service-loads/import`;
    loadsFinancialsImportApiUrl = `${this.functionsUrl}/api/load-financials/import`;
    financeExportDateApiUrl = `${this.functionsUrl}/api/loads/update/finance-export-date`;
    commodityRatesImportApiUrl = `${this.functionsUrl}/api/commodity-rates/import`;
    loadAssignAndRateApiUrl = `${this.functionsUrl}/api/loads/update/assign-and-rate`;
    checkLoadHasMatchingLaneUrl = `${this.functionsUrl}/api/loads/has-matching-lane`;
    loadFinancialsExportApiUrl = `${this.functionsUrl}/api/financial-export`;
    vendorLoadFileDownloadApiUrl = `${this.functionsUrl}/api/vendor-load-file/download`;
    vendorLoadFileCanDownloadApiUrl = `${this.functionsUrl}/api/vendor-load-file/can-download`;
    customerLoadFileDownloadApiUrl = `${this.functionsUrl}/api/customer-load-file/download`;
    customerLoadFileCanDownloadApiUrl = `${this.functionsUrl}/api/customer-load-file/can-download`;
    loadNotificationUrl = `${this.functionsUrl}/api/load-notification/manual`;
    laneCreateUrl = `${this.functionsUrl}/api/lanes/create`;
    laneUpdateUrl = `${this.functionsUrl}/api/lanes/update`;
    laneDeleteUrl = `${this.functionsUrl}/api/lanes/delete`;
    metabaseUrl = `${this.functionsUrl}/api/metabase`;
    recalculateFormulasUrl = `${this.functionsUrl}/api/loads/formulae/recalculate`;

    constructor(
        private httpClient: HttpClient,
        private logger: LoggerService,
        private notificationsService: NotificationsService,
        private profile: ProfileProvider,
        @Inject(SCHN_FUNCTIONS_API_URL) private readonly functionsUrl: string
    ) {}

    getMetabaseUrl() {
        return this.profile.userGroupType$.pipe(
            filter(isValue),
            map(
                userGroupType =>
                    DashboardsByUserAccountType[
                        userGroupType as UserAccountType
                    ]
            ),
            switchMap(dashboard =>
                this.httpClient.get<string>(this.metabaseUrl, {
                    params: {
                        dashboard: dashboard || '',
                    },
                })
            )
        );
    }

    downloadFinancialExport(
        request: LoadFinancialsExportRequest
    ): Observable<boolean> {
        const earl = `${this.loadFinancialsExportApiUrl}?includeLoadData=${request.includeLoadData}&${request.loadsUnifiedQueryString}`;

        return this.httpClient.get(earl, { responseType: 'blob' }).pipe(
            map(blob => {
                saveAs(blob, 'Financial Load Import.xlsx', { autoBom: false });
                return true;
            }),
            catchError(error => {
                this.logger.error(error);
                return of(false);
            })
        );
    }

    downloadVendorLoadFile(vendorId: string) {
        const earl = `${this.vendorLoadFileDownloadApiUrl}?vendorId=${vendorId}`;

        return this.getCanDownloadVendorLoadFile(vendorId).pipe(
            switchMap(response => {
                if (response) {
                    return this.httpClient
                        .get(earl, { responseType: 'blob' })
                        .pipe(
                            map(blob => {
                                saveAs(blob, 'Load Update - Services.xlsx', {
                                    autoBom: false,
                                });

                                return true;
                            })
                        );
                }

                return of(false);
            }),
            catchError(error => {
                this.logger.error(error);
                return of(false);
            })
        );
    }

    getCanDownloadVendorLoadFile(vendorId: string) {
        const earl = `${this.vendorLoadFileCanDownloadApiUrl}?vendorId=${vendorId}`;
        return this.httpClient.get<boolean>(earl, {
            responseType: 'json',
            observe: 'body',
        });
    }

    downloadCustomerLoadFile(customerId: string) {
        const earl = `${this.customerLoadFileDownloadApiUrl}?customerId=${customerId}`;

        return this.getCanDownloadCustomerLoadFile(customerId).pipe(
            switchMap(response => {
                if (response) {
                    return this.httpClient
                        .get(earl, { responseType: 'blob' })
                        .pipe(
                            map(blob => {
                                saveAs(blob, 'Load Request Form.xlsx', {
                                    autoBom: false,
                                });

                                return true;
                            })
                        );
                }

                return of(false);
            }),
            catchError(error => {
                this.logger.error(error);
                return of(false);
            })
        );
    }

    getCanDownloadCustomerLoadFile(customerId: string) {
        const earl = `${this.customerLoadFileCanDownloadApiUrl}?customerId=${customerId}`;
        return this.httpClient.get<boolean>(earl, {
            responseType: 'json',
            observe: 'body',
        });
    }

    sendEmail$(loadId: string) {
        const earl = `${this.loadNotificationUrl}?loadId=${loadId}`;
        return this.httpClient.post(earl, {});
    }

    setFinanceExportDate(loadIds: string[]): Observable<string> {
        return this.httpClient
            .post<string>(
                this.financeExportDateApiUrl,
                {
                    loadIds,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            .pipe(
                tap(() =>
                    this.logger.info(
                        `Finance export date updated for loads ${loadIds.join(
                            ', '
                        )}`
                    )
                )
            );
    }

    runAutomationsForLane$(laneId: string) {
        return this.assignAndRateLoads$({ laneId });
    }

    runAutomationsForRate$(laneId: string, rateId: string) {
        return this.assignAndRateLoads$({ laneId, rateId });
    }

    runAutomationsForLoad$(loadId: string, updateExisting?: boolean) {
        return this.assignAndRateLoads$({ loadIds: [loadId], updateExisting });
    }

    checkLoadHasMatchingLane$(request: LoadHasLaneRequest) {
        if (!request.pickupStopLocationId?.length) {
            return of({ status: 'initial' } as LoadHasLaneResult);
        }

        return this.httpClient
            .post<LoadHasLaneResult>(
                this.checkLoadHasMatchingLaneUrl,
                request,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            .pipe(
                map(
                    result =>
                        ({
                            hasLane: result.hasLane || false,
                            status: 'complete',
                            laneId: result.laneId,
                        }) as LoadHasLaneResult
                )
            );
    }

    laneCreate(body: RadiusLaneCreateBody) {
        return this.httpClient.post<LaneRead>(this.laneCreateUrl, body);
    }

    laneUpdate(body: RadiusLaneUpdateBody) {
        return this.httpClient.post<LaneRead>(this.laneUpdateUrl, body).pipe(
            tap(updated => {
                if (updated.id !== body.laneId) {
                    this.notificationsService.create({
                        type: NotificationType.ToastMessage,
                        toastType: ToastType.info,
                        message:
                            'There are loads using this route configuration. Due to business processes a new route was created.',
                    } as ToastMessage);
                }
            })
        );
    }

    laneDelete(laneId: string) {
        return this.httpClient.post<LaneRead>(this.laneDeleteUrl, { laneId });
    }

    private assignAndRateLoads$(params: {
        loadIds?: string[];
        laneId?: string;
        rateId?: string;
        updateExisting?: boolean;
    }) {
        const isCustomer =
            this.profile.profile.userGroupType === UserAccountType.Customer;
        const loadingMessageRef = this.notificationsService.create({
            type: NotificationType.ToastMessage,
            toastType: ToastType.loading,
            message: isCustomer
                ? 'Processing load request...'
                : 'Processing automations...',
            persist: true,
        } as ToastMessage);

        this.logger.info(`Running automations for ${JSON.stringify(params)}`);
        return this.httpClient
            .post<LoadRateResult[]>(this.loadAssignAndRateApiUrl, params, {
                headers: {
                    'Content-Type': 'application/json',
                },
            })
            .pipe(
                tap(() =>
                    this.logger.info(
                        `Automations run using params ${JSON.stringify(params)}`
                    )
                ),
                map(result => {
                    if (result.length === 0) {
                        return {
                            success: 0,
                            error: 0,
                            failureMessage: isCustomer
                                ? 'No service provider found for the requested load.'
                                : 'No loads found to rate.',
                        };
                    }
                    if ((result as SafeAny).error) {
                        return {
                            success: 0,
                            error: 0,
                            failureMessage: (result as SafeAny).error,
                        };
                    }
                    return result.reduce(
                        (acc, curr) => {
                            if (curr.Load) {
                                acc.success++;
                            } else if (curr.Message) {
                                acc.error++;
                            }
                            return acc;
                        },
                        { success: 0, error: 0 } as {
                            success: number;
                            error: number;
                        }
                    );
                }),
                catchError(err => {
                    this.notificationsService.remove(loadingMessageRef);
                    return throwError(() => err);
                }),
                tap(
                    (result: {
                        success: number;
                        error: number;
                        failureMessage?: string;
                    }) => {
                        this.notificationsService.remove(loadingMessageRef);
                        this.notificationsService.create({
                            type: NotificationType.ToastMessage,
                            toastType: result.failureMessage
                                ? ToastType.error
                                : ToastType.success,
                            message: result.failureMessage
                                ? result.failureMessage
                                : isCustomer
                                  ? 'Load has been assigned for pickup.'
                                  : `Rate automations completed. ${
                                        result.success
                                            ? result.success === 1 &&
                                              result.error === 0
                                                ? 'Load successfully rated. '
                                                : `${result.success} load(s) successfully rated. `
                                            : ''
                                    } ${
                                        result.error
                                            ? result.error === 1 &&
                                              result.success === 0
                                                ? 'Load could not be auto rated.'
                                                : `${result.error} load(s) could not be rated.`
                                            : ''
                                    }`,
                        } as ToastMessage);
                    }
                )
            );
    }

    recalculateFinancials$(requestParams: Partial<LoadSearchParams>) {
        return this.httpClient
            .post(this.recalculateFormulasUrl, requestParams)
            .pipe(
                tap(() => {
                    this.notificationsService.create({
                        type: NotificationType.ToastMessage,
                        message: `Processing your request. You will receive an email when it is complete.`,
                        toastType: ToastType.success,
                    } as ToastMessage);
                })
            );
    }
}
